// The Klaviyo handover panel's rules, apart from the panel so they run on Node
// in the admin tests (tests/admin/test_handover_panel_model.py), and the waves'
// (tests/admin/test_waves_panel_model.py).

import type { CountryWaves, Handover, WaveJob, WavePreview, WaveSync } from "@/lib/api";
import { count } from "@/components/emc/format";

const RANGE = "N egy hullám száma 0 és 7 között.";

/** N as typed: a whole wave from 0 to 7, or a sentence saying what is wrong.
 * `current` is the N it would replace; the same N again is not a change. */
export function waveOf(text: string, current: number | null): number {
  const trimmed = text.trim();
  if (!/^\d+$/.test(trimmed)) throw new Error(RANGE);
  const wave = Number(trimmed);
  if (wave > 7) throw new Error(RANGE);
  if (current !== null && wave === current) throw new Error(`N már most is ${current}.`);
  return wave;
}

/** What the screens say about a parallel country (`PARALLEL_MESSAGE`). */
export const PARALLEL = "a Klaviyo is küld nekik — nincs átadás-szűrés";

/** Who we may mail in the country, in one sentence. */
export function meaningOf(row: Pick<Handover, "restricts" | "wave" | "parallel">): string {
  if (row.parallel) return `mindenkinek, akit más nem zár ki: ${PARALLEL}`;
  if (!row.restricts) return "mindenkinek: a Klaviyo már nem küld";
  if (row.wave === 0) return "senkinek: a Klaviyo még mindenkinek küld";
  return `akinek a hullámát (legfeljebb ${row.wave}.) a Klaviyo már megkapta`;
}

// --- The waves (0043): the figures, the switch, the jobs -----------------------------

/** Who raising N would let through, in one sentence; null when nothing restricts. */
export function readinessOf(
  waves: Pick<CountryWaves, "restricts" | "n" | "mailable_now" | "next_wave" | "opens_next" | "waiting_next">,
): string | null {
  if (!waves.restricts || waves.n === null) return null;
  const now =
    waves.n === 0
      ? "N = 0: az átadáson át most senkinek nem küldünk."
      : `N = ${waves.n}: az átadáson át most ${count(waves.mailable_now ?? 0)} kontaktnak küldhetünk.`;
  if (waves.next_wave === null) return now;
  const opens = `N = ${waves.next_wave} további ${count(waves.opens_next ?? 0)} kontaktot nyitna meg`;
  const waiting = waves.waiting_next
    ? ` (${count(waves.waiting_next)} még vár az emc_wave írására, ők csak utána jönnek)`
    : "";
  return `${now} ${opens}${waiting}.`;
}

/** What the klaviyo-sync loop is doing with the switch, in one sentence. */
export function loopSentence(sync: Pick<WaveSync, "enabled" | "loop_status" | "loop_seen_at">): string {
  if (!sync.enabled) return "Kikapcsolva: a Klaviyóba semmi nem íródik.";
  switch (sync.loop_status) {
    case null:
      return "Bekapcsolva; a klaviyo-sync még nem jelentkezett erre az országra.";
    case "writing":
      return "Bekapcsolva: a klaviyo-sync írja az emc_wave-et.";
    case "idle":
      return "Bekapcsolva: nincs mit írni, minden hullámos kontakt értesítve.";
    case "gate_closed":
      return "Bekapcsolva, de a globális Klaviyo-írás tiltva (EMC_KLAVIYO_PUSH_ENABLED): semmi nem íródik.";
    case "no_key":
      return "Bekapcsolva, de az országnak nincs Klaviyo API-kulcsa: semmi nem íródik.";
    case "rate_limited":
      return "Bekapcsolva: a Klaviyo várakoztat (rate limit), a következő körben folytatja.";
    case "failed":
      return "Bekapcsolva, de az utolsó írás hibára futott; a következő körben újra próbálja.";
    default:
      return `Bekapcsolva: ${sync.loop_status}.`;
  }
}

/** How far a job is, 0-100, or null when it is not known. */
export function progressOf(job: Pick<WaveJob, "state" | "total" | "done">): number | null {
  if (job.state === "done") return 100;
  if (!job.total) return null;
  return Math.min(100, Math.round((job.done / job.total) * 100));
}

/** The dry run or a job's result, in the CLI's categories and order. */
export function outcomeRows(
  values: Pick<WavePreview, "stamped" | "already_earlier" | "not_subscribed" | "not_found">,
  done: boolean,
): { label: string; value: number }[] {
  return [
    { label: done ? "kijelölve" : "kijelölésre kerülne", value: values.stamped },
    { label: "már korábbi hullámban", value: values.already_earlier },
    { label: "nincs feliratkozva", value: values.not_subscribed },
    { label: "nem kontakt ebben az országban", value: values.not_found },
  ];
}

/** A job's wave as typed: a whole wave from 1 to 7. */
export function jobWaveOf(text: string): number {
  const trimmed = text.trim();
  if (!/^\d+$/.test(trimmed)) throw new Error("A hullám egy szám 1 és 7 között.");
  const wave = Number(trimmed);
  if (wave < 1 || wave > 7) throw new Error("A hullám egy szám 1 és 7 között.");
  return wave;
}
